import * as z from "zod";

/** Lets the UI distinguish a real zero from a source that could not be reached. */
export const blockStatusSchema = z.enum(["ok", "unavailable"]);
export type BlockStatus = z.infer<typeof blockStatusSchema>;

export const orderEditingKeyCountSchema = z.object({
  key: z.string(),
  label: z.string(),
  count: z.number(),
});

export const orderEditingMerchantRowSchema = z.object({
  teamId: z.string(),
  name: z.string(),
  storeUrl: z.string().nullable(),
  /** Enabled means a tool is on and a surface is on; silent means enabled with no edits. */
  status: z.enum(["active", "silent", "disabled_with_history"]),
  customized: z.boolean(),
  tools: z.array(z.string()),
  platforms: z.array(z.string()),
  edits: z.number(),
  upsellEdits: z.number(),
  upsellRevenueUsd: z.number().nullable(),
  cancels: z.number(),
  topSource: z.string().nullable(),
  currency: z.string().nullable(),
  firstEditAt: z.coerce.date().nullable(),
  lastEditAt: z.coerce.date().nullable(),
});

export type OrderEditingMerchantRow = z.infer<
  typeof orderEditingMerchantRowSchema
>;

export const orderEditingSummarySchema = z.object({
  edits: z.number(),
  upsellEdits: z.number(),
  upsellRevenueUsd: z.number(),
  cancels: z.number(),
  supportSavingsUsd: z.number(),
});

export const getOrderEditingDashboard = {
  name: "getOrderEditingDashboard" as const,
  input: z.object({
    startDate: z.date(),
    endDate: z.date(),
    includeSilentMerchants: z.boolean().default(false),
    pageNumber: z.number().int().min(0).default(0),
    pageSize: z.number().int().min(1).max(200).default(50),
  }),
  output: z.object({
    activity: z.object({
      status: blockStatusSchema,
      current: orderEditingSummarySchema,
      prior: orderEditingSummarySchema,
      byKind: z.array(orderEditingKeyCountSchema),
      bySource: z.array(orderEditingKeyCountSchema),
      timeSeries: z.array(
        z.object({
          date: z.string(),
          edits: z.number(),
          upsellRevenueUsd: z.number(),
        }),
      ),
      interval: z.enum(["day", "week", "month"]),
      activeMerchants: z.number(),
      teamsTruncated: z.number(),
      lastIndexedAt: z.coerce.date().nullable(),
    }),
    adoption: z.object({
      status: blockStatusSchema,
      enabledMerchants: z.number(),
      activeMerchants: z.number(),
    }),
    fx: z.object({
      status: blockStatusSchema,
      skippedCurrencies: z.array(z.string()),
      /** Share of upsell edits whose rows carry a currency, so pre-fix gaps are visible. */
      revenueCoveragePct: z.number().nullable(),
    }),
    rows: z.array(orderEditingMerchantRowSchema),
    totalRowCount: z.number(),
  }),
};

export type GetOrderEditingDashboardOutput = z.infer<
  typeof getOrderEditingDashboard.output
>;
